import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wrench, Storefront, ThumbsUp, MapPin, Phone, EnvelopeSimple, Globe, Check, X, Trash, User } from "@phosphor-icons/react";
import { AdminHeader, PrimaryButton, GhostButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { capLabel } from "@/lib/repairerMeta";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "declined", label: "Declined" },
];

const TYPE_META = {
  application: { label: "Repairer application", Icon: Storefront, cls: "text-sky-300 bg-sky-500/10 border-sky-500/30" },
  recommendation: { label: "Customer recommendation", Icon: ThumbsUp, cls: "text-[#73bf44] bg-[#73bf44]/10 border-[#73bf44]/30" },
};

export default function AdminSubmissions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    adminApi.get(`/admin/repairer-submissions?status=${tab}`)
      .then(({ data }) => setRows(data))
      .catch((e) => { setRows([]); toast.error(getErrorMessage(e, "Couldn't load submissions. Check your connection and retry.")); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const review = async (r, action) => {
    setBusyId(r.id);
    try {
      await adminApi.put(`/admin/repairer-submissions/${r.id}`, { action });
      toast.success(action === "approve" ? `Added “${r.business_name}” to the Repair Network` : "Submission declined");
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setBusyId(null); }
  };

  const del = async (r) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    await adminApi.delete(`/admin/repairer-submissions/${r.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <AdminHeader
        title="Submissions"
        subtitle="Repairer applications and customer recommendations. Approve to add them to the Repair Network map."
      />
      <div className="p-8">
        <div className="flex gap-2 mb-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`submissions-tab-${t.key}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                tab === t.key ? "bg-[#73bf44] text-[#050505]" : "border border-neutral-800 text-neutral-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-neutral-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="text-neutral-600 border border-dashed border-neutral-800 rounded-xl py-16 text-center flex flex-col items-center gap-3" data-testid="submissions-empty">
            <Wrench size={36} className="text-neutral-700" weight="duotone" /> No {tab} submissions.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="submissions-list">
            {rows.map((r) => {
              const meta = TYPE_META[r.type] || TYPE_META.application;
              const Icon = meta.Icon;
              return (
                <div key={r.id} data-testid="submission-card" className="bg-[#0F0F0F] border border-neutral-800 rounded-xl p-5">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className={`inline-flex items-center gap-1.5 text-[0.65rem] font-semibold px-2.5 py-1 rounded-full border ${meta.cls}`}>
                      <Icon size={13} weight="fill" /> {meta.label}
                    </span>
                    <span className="text-[0.65rem] text-neutral-600">{(r.created_at || "").slice(0, 10)}</span>
                  </div>

                  <div className="font-heading text-lg font-bold text-white">{r.business_name}</div>
                  <div className="flex items-start gap-1.5 text-sm text-neutral-400 mt-1">
                    <MapPin size={14} className="text-neutral-500 mt-0.5 shrink-0" />
                    <span>{[r.address, r.suburb, r.state, r.postcode].filter(Boolean).join(", ") || "—"}</span>
                  </div>

                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm">
                    {r.phone && <span className="inline-flex items-center gap-1.5 text-neutral-300"><Phone size={13} className="text-[#73bf44]" /> {r.phone}</span>}
                    {r.email && <span className="inline-flex items-center gap-1.5 text-neutral-300"><EnvelopeSimple size={13} className="text-[#73bf44]" /> {r.email}</span>}
                    {r.website && <span className="inline-flex items-center gap-1.5 text-neutral-300"><Globe size={13} className="text-[#73bf44]" /> {r.website}</span>}
                  </div>

                  {(r.capabilities || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {r.capabilities.map((k) => (
                        <span key={k} className="text-[0.6rem] font-medium px-1.5 py-0.5 rounded bg-neutral-800 text-neutral-300">{capLabel(k)}</span>
                      ))}
                    </div>
                  )}
                  {r.brands && <div className="text-xs text-neutral-500 mt-2">Brands: {r.brands}</div>}
                  {r.contact_name && <div className="text-xs text-neutral-500 mt-2">Contact: {r.contact_name}</div>}
                  {r.notes && <div className="text-sm text-neutral-400 mt-3 border-l-2 border-neutral-800 pl-3">{r.notes}</div>}
                  {r.type === "recommendation" && (r.submitter_name || r.submitter_email) && (
                    <div className="text-xs text-neutral-500 mt-3 flex items-center gap-1.5">
                      <User size={13} /> Recommended by {r.submitter_name || "a customer"}{r.submitter_email ? ` · ${r.submitter_email}` : ""}
                    </div>
                  )}

                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-neutral-800">
                    {r.status === "pending" ? (
                      <>
                        <PrimaryButton onClick={() => review(r, "approve")} disabled={busyId === r.id} data-testid="submission-approve" className="px-4 py-2">
                          <Check size={15} weight="bold" /> Approve
                        </PrimaryButton>
                        <GhostButton onClick={() => review(r, "decline")} disabled={busyId === r.id} data-testid="submission-decline" className="px-4 py-2">
                          <X size={15} /> Decline
                        </GhostButton>
                      </>
                    ) : (
                      <span className={`text-xs font-semibold capitalize ${r.status === "approved" ? "text-[#73bf44]" : "text-neutral-500"}`}>
                        {r.status}{r.status === "approved" ? " · added to map" : ""}
                      </span>
                    )}
                    <button onClick={() => del(r)} data-testid="submission-delete" className="ml-auto p-2 rounded-lg text-neutral-500 hover:text-red-400 hover:bg-red-500/10">
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
